import React, { useState } from 'react';
import { AlertTriangle, Scale, DollarSign, Target, ShoppingBag, Zap, Send, MessageCircle } from 'lucide-react';
import CounterAnimation from './CounterAnimation';
import RegistrationModal from './RegistrationModal';

const WhyImportantSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };
  const reasons = [
    {
      icon: AlertTriangle,
      title: 'Avoid Brand Confusion',
      description: 'Without proper trademark protection, competitors may adopt similar names or logos, leading to customer confusion and weakening your brand identity. A registered trademark ensures you hold exclusive rights to your brand mark.',
      statNum: 70,
      statSuffix: '%',
      statLabel: 'of companies experience brand identity confusion'
    },
    {
      icon: Scale,
      title: 'Stronger Legal Enforcement',
      description: 'Federal trademark registration gives you the legal authority to pursue infringement cases in federal court and may allow recovery of statutory damages along with legal fees.',
      statNum: 3,
      statSuffix: 'x',
      statLabel: 'higher success rate in infringement disputes'
    },
    {
      icon: DollarSign,
      title: 'Instant Expert Support Available',
      description: 'With our support, you get round-the-clock assistance for all your trademark needs, ensuring quick responses, expert guidance, and uninterrupted protection for your brand.',
      statNum: 24,
      statSuffix: '/7',
      statLabel: 'Instant expert help whenever you need it.'
    }
  ];

  const risks = [
    {
      icon: Target,
      text: 'Another party may register your brand name before you do'
    },
    {
      icon: ShoppingBag,
      text: 'Rival businesses may create marketplace confusion using similar branding'
    },
    {
      icon: Zap,
      text: 'You may spend heavily on marketing a brand that you cannot legally safeguard'
    }
  ];

  return (
    <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-[#168356]/10 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">
            Essential Insights
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Why Trademark Registration Matters
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            In today's highly competitive business environment, securing a trademark is no longer optional—it is a vital step for every serious business owner
          </p>
        </div>

        {/* Main Reasons */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {reasons.map((reason, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 group"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-16 h-16 bg-[#168356]/10 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                  <reason.icon className="w-8 h-8 text-[#168356]" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-[#168356]">
                    <CounterAnimation 
                      end={reason.statNum} 
                      prefix={reason.statPrefix || ''} 
                      suffix={reason.statSuffix || ''} 
                    />
                  </div>
                  <div className="text-xs text-gray-500">{reason.statLabel}</div>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">
                {reason.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {reason.description}
              </p>
            </div>
          ))}
        </div>

        {/* Risk Section */}
        <div className="bg-gradient-to-br from-red-50 to-green-50 rounded-2xl p-8 md:p-12 border-2 border-red-100">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Header + Risks */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-black">
                  The Risk of Delaying Protection
                </h3>
              </div>
              <p className="text-lg text-gray-700 mb-6">
                Every single day without securing your trademark exposes your brand to unnecessary risk. Below are potential consequences you may face:
              </p>
              <div className="space-y-4">
                {risks.map((risk, index) => (
                  <div key={index} className="flex items-start gap-3 group cursor-default">
                    <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                      <risk.icon className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-gray-700 pt-1">{risk.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Real-World Impact */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h4 className="text-2xl font-bold text-black mb-6 text-center">
                Real-World Impact
              </h4>
              <div className="space-y-6">
                <div className="border-l-4 border-[#168356] pl-6">
                  <p className="text-gray-700 italic mb-2">
                    "A competitor began using a name very close to ours. Without any trademark protection in place, we had no legal option and were forced to completely rebrand."
                  </p>
                  <p className="text-sm text-gray-500">- Jason R. Caldwell, Online Business Owner</p>
                </div>
                <div className="border-l-4 border-[#168356] pl-6">
                  <p className="text-gray-700 italic mb-2">
                    "We invested $150K into marketing efforts before realizing the trademark was already owned by someone else. Restarting everything from scratch was extremely damaging."
                  </p>
                  <p className="text-sm text-gray-500">- Michael J. Harrington, Technology Startup Founder</p>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                <p className="text-2xl font-bold text-[#168356] mb-2">Don't Let This Be You</p>
                <p className="text-gray-600">Start your trademark registration today</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Buttons - Below the entire section */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => openModal('registration')}
            className="bg-[#d29b00] text-white px-8 py-4 rounded-lg font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
          >
            <Send className="w-5 h-5" />
            Start Registration Now
          </button>
        </div>
      </div>

      {/* Registration Modal */}
      <RegistrationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
      />
    </div>
  );
};

export default WhyImportantSection;